import { partById } from "../parts/PartCatalog";
import { airDensity, bodyById, bodyPosition, gravity } from "../world/SolarSystem";
import type { Vessel } from "./Vessel";

const G0 = 9.80665;

export interface PhysicsResult {
  thrustN: number;
  massFlow: number;
  dragN: number;
  heatQ: number; // W/m² ish estimate
}

/** Integrate one physics step. dt is simulation seconds. */
export function stepVessel(vessel: Vessel, dt: number): PhysicsResult {
  const body = bodyById(vessel.parentId);

  // ----- Gravity -----
  const g = gravity(body, vessel.rx, vessel.ry);
  let ax = g.ax, ay = g.ay;

  // ----- Thrust -----
  const engines = vessel.activeEngines();
  let thrustN = 0;
  let massFlow = 0;
  const alt = vessel.altitude(body.radius);

  for (const e of engines) {
    const def = partById(e.partId);
    if (!def.thrust) continue;
    const atm = body.airDensitySL > 0 && alt < body.atmosphereHeight ? Math.exp(-alt / body.scaleHeight) : 0;
    const asl = def.ispAsl ?? def.ispVac ?? 280;
    const vac = def.ispVac ?? asl;
    const isp = atm * asl + (1 - atm) * vac;
    const throttle = def.throttleable ? vessel.throttle : 1;
    const t = def.thrust * throttle * (atm * (asl / vac) + (1 - atm));
    thrustN += t;
    massFlow += t / (isp * G0);
  }

  // Thrust direction = heading (nose direction)
  const thrustAccel = thrustN / vessel.mass;
  ax += thrustAccel * Math.sin(vessel.heading);
  ay += thrustAccel * Math.cos(vessel.heading);

  // ----- Drag -----
  const rho = airDensity(body, alt);
  const speed = Math.hypot(vessel.vx, vessel.vy);
  let dragN = 0;
  let heatQ = 0;
  if (rho > 0 && speed > 1) {
    let cd = 0;
    let chuteMult = 0;
    for (const p of vessel.attachedParts) {
      const d = partById(p.partId);
      cd += d.drag ?? 0;
      if (d.chuteDragMult && vessel.parachuteDeployed) chuteMult = Math.max(chuteMult, d.chuteDragMult);
    }
    const effectiveCd = cd + chuteMult;
    // F = 0.5 * rho * v² * Cd * A. We fold A into Cd — close enough.
    dragN = 0.5 * rho * speed * speed * effectiveCd;
    const dragAccel = dragN / vessel.mass;
    ax -= dragAccel * (vessel.vx / speed);
    ay -= dragAccel * (vessel.vy / speed);

    // Heating: crude. q ∝ rho * v³
    heatQ = rho * speed * speed * speed * 1e-6; // scaled
  }

  // ----- Integrate -----
  vessel.vx += ax * dt;
  vessel.vy += ay * dt;
  vessel.rx += vessel.vx * dt;
  vessel.ry += vessel.vy * dt;

  // ----- Consume fuel -----
  if (massFlow > 0) {
    let remainingFlow = massFlow * dt;
    // Solid boosters each drain their own pool proportionally to their thrust share.
    const solidEngines = engines.filter((e) => partById(e.partId).fuelKind === "solid");
    if (solidEngines.length > 0) {
      let solidThrust = 0;
      for (const e of solidEngines) solidThrust += partById(e.partId).thrust ?? 0;
      for (const e of solidEngines) {
        const def = partById(e.partId);
        const share = (def.thrust ?? 0) / solidThrust;
        const used = remainingFlow * share;
        const have = vessel.fuel.solidByUid.get(e.uid) ?? 0;
        vessel.fuel.solidByUid.set(e.uid, Math.max(0, have - used));
      }
    }
    // Liquid engines drain the shared liquid pool.
    const liquidEngines = engines.filter((e) => partById(e.partId).fuelKind !== "solid");
    if (liquidEngines.length > 0) {
      let liqThrust = 0;
      for (const e of liquidEngines) liqThrust += partById(e.partId).thrust ?? 0;
      const share = liqThrust / Math.max(thrustN, 1);
      vessel.fuel.liquid = Math.max(0, vessel.fuel.liquid - remainingFlow * share);
    }
  }

  // ----- Reentry heat accumulation -----
  const surfaceMaxTemp = Math.max(
    ...vessel.attachedParts.map((p) => partById(p.partId).maxTemp ?? 1500),
  );
  // Simplistic: track peak q translated into an effective stagnation temperature (K-ish).
  const stagTemp = 300 + heatQ * 120;
  vessel.peakTempK = Math.max(vessel.peakTempK, stagTemp);
  if (stagTemp > surfaceMaxTemp) {
    vessel.destroyed = true;
  }

  // ----- Ground check -----
  const altPost = vessel.altitude(body.radius);
  if (altPost <= 0) {
    const speedAtImpact = Math.hypot(vessel.vx, vessel.vy);
    // Snap back to surface
    const r = Math.hypot(vessel.rx, vessel.ry);
    const scale = body.radius / r;
    vessel.rx *= scale;
    vessel.ry *= scale;
    // Kill radial velocity (bounce-free sticky ground)
    const nx = vessel.rx / body.radius;
    const ny = vessel.ry / body.radius;
    const vn = vessel.vx * nx + vessel.vy * ny;
    vessel.vx -= vn * nx;
    vessel.vy -= vn * ny;

    if (speedAtImpact > 10 && !vessel.landed) {
      vessel.destroyed = true;
    } else {
      vessel.landed = true;
      vessel.vx = 0;
      vessel.vy = 0;
    }
  }

  vessel.met += dt;
  return { thrustN, massFlow, dragN, heatQ };
}

/** Switch the vessel's reference body (SOI change). Applies a frame transform. */
export function changeReferenceBody(vessel: Vessel, newParentId: string, tNow: number) {
  if (vessel.parentId === newParentId) return;
  const oldBody = bodyById(vessel.parentId);
  const newBody = bodyById(newParentId);

  // Compute old parent position in new parent's frame.
  // We only handle one-level parent/child transitions here (earth<->moon, sun<->earth, etc.)
  if (oldBody.parent === newParentId) {
    // Old was child of new. Add old body's position.
    const pos = bodyPosition(oldBody, tNow);
    vessel.rx += pos.x;
    vessel.ry += pos.y;
  } else if (newBody.parent === vessel.parentId) {
    // New is child of old. Subtract new body's position.
    const pos = bodyPosition(newBody, tNow);
    vessel.rx -= pos.x;
    vessel.ry -= pos.y;
  }
  vessel.parentId = newParentId;
}
